import { useEffect, useRef, useState, useCallback } from 'react';

export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold: options.threshold || 0.15, rootMargin: options.rootMargin || '0px 0px -60px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
}

export function useParallax() {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const windowH = window.innerHeight;
    if (rect.top < windowH && rect.bottom > 0) {
      const progress = (windowH - rect.top) / (windowH + rect.height);
      setOffset((progress - 0.5) * 80);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return [ref, offset];
}

export function ScrollReveal({ children, className = '', delay = 0, direction = 'up', scale = false }) {
  const [ref, isVisible] = useScrollReveal();
  const transforms = {
    up: 'translateY(40px)',
    down: 'translateY(-40px)',
    left: 'translateX(40px)',
    right: 'translateX(-40px)',
    none: 'translateY(0)',
  };

  return (
    <div ref={ref} className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        WebkitTransform: isVisible ? `translateY(0) translateX(0) scale(1)` : `${transforms[direction]} ${scale ? 'scale(0.95)' : 'scale(1)'}`,
        transform: isVisible ? `translateY(0) translateX(0) scale(1)` : `${transforms[direction]} ${scale ? 'scale(0.95)' : 'scale(1)'}`,
        WebkitTransition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
        transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      }}>
      {children}
    </div>
  );
}

export function TextReveal({ children, className = '', tag = 'span' }) {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.3 });
  const Tag = tag;
  return (
    <Tag ref={ref} className={`inline-block overflow-hidden ${className}`}>
      <span style={{
        display: 'inline-block',
        transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        opacity: isVisible ? 1 : 0,
        transition: 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        {children}
      </span>
    </Tag>
  );
}
